import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import {
  deleteRequest,
  getErrorMessage,
  getRequest,
  patchRequest,
  postRequest,
  PROJECTS_API,
} from "./api";
import {
  actionButtonSx,
  PageShell,
  panelSx,
  statusChip,
} from "./ProjectsCommon";
import { can } from "../../../auth/can";
import { Close } from "@mui/icons-material";

const emptyForm = {
  customer_id: "",
  name: "",
  code: "",
  project_type: "",
  status: "PLANNED",
  target_devices: "",
  commissioned_devices: "",
  start_date: "",
  target_end_date: "",
  project_manager: "",
  description: "",
  notes: "",
};

const PROJECT_STATUS = [
  "PLANNED",
  "IN_PROGRESS",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
];

function DeploymentsSkeleton() {
  const projectSkeletons = Array.from({ length: 3 });

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={30} />
            <Skeleton variant="text" width="46%" height={22} />
          </Box>
          <Skeleton
            variant="rounded"
            width={280}
            height={40}
            sx={{ maxWidth: "100%" }}
          />
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(auto-fit, minmax(320px, 1fr))",
          },
          gap: 2,
          width: "100%",
        }}
      >
        {projectSkeletons.map((_, index) => (
          <Box key={index} sx={{ width: "100%" }}>
            <Paper elevation={0} sx={{ ...panelSx, p: 2.25 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Skeleton variant="text" width="36%" height={30} />
                  <Skeleton variant="text" width="48%" height={22} />
                </Box>
                <Skeleton
                  variant="rounded"
                  width={98}
                  height={26}
                  sx={{ borderRadius: 999 }}
                />
              </Stack>

              <Skeleton
                variant="text"
                width="62%"
                height={22}
                sx={{ mt: 1.3 }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1.2 }}
              >
                <Skeleton variant="text" width="18%" height={20} />
                <Skeleton variant="text" width={38} height={20} />
              </Stack>
              <Skeleton
                variant="rounded"
                width="100%"
                height={8}
                sx={{ mt: 0.8, borderRadius: 999 }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                sx={{ mt: 2 }}
              >
                <Skeleton
                  variant="rounded"
                  width={118}
                  height={38}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton
                  variant="rounded"
                  width={82}
                  height={38}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton
                  variant="rounded"
                  width={88}
                  height={38}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            </Paper>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

export default function ProjectsDeployments() {
  const { activeOrg, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("authToken");
  const queryCustomerId =
    new URLSearchParams(location.search).get("customer_id") || "";
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [Saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [SelctedProject, setSelectPoroject] = useState(null);
  const [customerFilter, setCustomerFilter] = useState(
    queryCustomerId || "ALL",
  );
  const showBackToSites = Boolean(location.state?.fromSites);
  const canCreateProject = can(permissions, "projects.deployment.create");
  const canUpdateProject = can(permissions, "projects.deployment.update");
  const canDeleteProject = can(permissions, "projects.deployment.delete");

  useEffect(() => {
    setCustomerFilter(queryCustomerId || "ALL");
  }, [queryCustomerId]);

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [projectData, customerData] = await Promise.all([
        getRequest(`${PROJECTS_API}/deployments`, token, activeOrg),
        getRequest(`${PROJECTS_API}/customers`, token, activeOrg),
      ]);
      setProjects(projectData?.projects || []);
      setCustomers(customerData?.customers || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load projects"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!error && !success) return undefined;

    const timer = window.setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [error, success]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
    setErrors({});
  };

  const handleEdit = (project) => {
    setEditingId(project.id);
    setForm({
      customer_id: project.customer_id || "",
      name: project.name || "",
      code: project.code || "",
      project_type: project.project_type || "",
      status: project.status || "PLANNED",
      target_devices: project.target_devices || "",
      commissioned_devices: project.commissioned_devices || "",
      start_date: project.start_date || "",
      target_end_date: project.target_end_date || "",
      project_manager: project.project_manager || "",
      description: project.description || "",
      notes: project.notes || "",
    });
    setOpen(true);
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Required";
    if (!form.code.trim()) newErrors.code = "Required";
    if (!form.customer_id.trim()) newErrors.customer_id = "Required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setSaving(true);
    // if (!form.customer_id || !form.name.trim() || !form.code.trim()) {
    //   setError("Customer, project name, and code are required");
    //   return;
    // }
    const payload = {
      ...form,
      target_devices: Number(form.target_devices || 0),
      commissioned_devices: Number(form.commissioned_devices || 0),
    };
    try {
      if (editingId) {
        await patchRequest(
          `${PROJECTS_API}/deployments/${editingId}`,
          payload,
          token,
          activeOrg,
        );
        setSuccess("Project updated");
      } else {
        await postRequest(
          `${PROJECTS_API}/deployments`,
          payload,
          token,
          activeOrg,
        );
        setSuccess("Project created");
      }
      setOpen(false);
      await loadData();
      setErrors({});
    } catch (err) {
      setError(getErrorMessage(err, "Unable to save project"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!SelctedProject) return;
    setDeleting(true);
    // if (!window.confirm(`Delete project ${project.name}?`)) return;
    try {
      await deleteRequest(
        `${PROJECTS_API}/deployments/${SelctedProject.id}`,
        token,
        activeOrg,
      );
      setSuccess("Project deleted");
      setDeleteOpen(false);
      setSelectPoroject(null);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete project"));
    } finally {
      setDeleting(false);
    }
  };

  const visibleProjects = projects.filter((project) =>
    customerFilter === "ALL" ? true : project.customer_id === customerFilter,
  );

  return (
    <PageShell
      title="Projects"
      subtitle="Define deployment programs and track rollout progress before field commissioning begins."
      leadingAction={
        showBackToSites ? (
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#DBEAFE",
              fontWeight: 800,
              textTransform: "none",
              px: 0,
              minWidth: "auto",
            }}
          >
            Back to Sites
          </Button>
        ) : null
      }
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            ...actionButtonSx,
            width: { xs: "100%", sm: "auto" },
            justifyContent: "center",
          }}
        >
          Add Project
        </Button>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      {loading ? (
        <DeploymentsSkeleton />
      ) : (
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
                >
                  Project Portfolio
                </Typography>
                <Typography sx={{ mt: 0.35, color: "#64748B", fontSize: 13 }}>
                  {visibleProjects.length} project
                  {visibleProjects.length === 1 ? "" : "s"} in current view
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Customer"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                sx={{
                  width: { xs: "100%", md: 280 },
                  minWidth: { md: 280 },
                }}
              >
                <MenuItem value="ALL">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(auto-fit, minmax(320px, 1fr))",
              },
              gap: 2,
              width: "100%",
            }}
          >
            {visibleProjects.map((project) => {
              const progress = project.summary?.progress_percent || 0;

              return (
                <Box key={project.id} sx={{ width: "100%" }}>
                  <Paper elevation={0} sx={{ ...panelSx, p: 2.25 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Box sx={{ minWidth: 0, width: "100%" }}>
                        <Typography
                          sx={{
                            fontSize: 18,
                            fontWeight: 800,
                            color: "#0F172A",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {project.name}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: 12.5,
                            color: "#64748B",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {project.customer?.name || "Customer pending"} /{" "}
                          {project.code}
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>{statusChip(project.status)}</Box>
                    </Stack>

                    <Typography
                      sx={{
                        mt: 1.3,
                        fontSize: 13,
                        color: "#334155",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {project.project_type || "Project"} / Target{" "}
                      {project.target_devices || 0} / Commissioned{" "}
                      {project.commissioned_devices || 0}
                    </Typography>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mt: 1.2 }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        {project.summary?.site_count || 0} sites
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}
                      >
                        {progress}%
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        mt: 0.8,
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: "#E2E8F0",
                      }}
                    />

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.2}
                      sx={{ mt: 2 }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<OpenInNewRoundedIcon />}
                        onClick={() =>
                          navigate(`/projects/deployments/${project.id}`)
                        }
                        sx={{
                          fontWeight: 800,
                          backgroundColor: "#0F172A",
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Dashboard
                      </Button>

                      <Button
                        variant="contained"
                        onClick={() => handleEdit(project)}
                        sx={{
                          fontWeight: 800,
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setSelectPoroject(project);
                          setDeleteOpen(true);
                        }}
                        sx={{
                          fontWeight: 800,
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={fullScreenDialog}
        maxWidth="md"
        PaperProps={{
          sx: {
            ...panelSx,
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 32px)" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
            borderRadius: { xs: 0, sm: panelSx.borderRadius },
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#fff",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          {editingId ? "Edit Project" : "Create Project"}

          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              color: "#fff",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
              mt: 1.5,
            }}
          >
            {/* Customer */}
            <TextField
              select
              required
              fullWidth
              label="Customer"
              value={form.customer_id}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  customer_id: e.target.value,
                }));
                setErrors((prev) => ({ ...prev, customer_id: "" }));
              }}
              error={!!errors.customer_id}
              helperText={errors.customer_id || ""}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Project Code */}
            <TextField
              fullWidth
              required
              label="Project Code"
              value={form.code}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }));
                setErrors((prev) => ({ ...prev, code: "" }));
              }}
              error={!!errors.code}
              helperText={errors.code || ""}
            />

            {/* Project Name */}
            <TextField
              fullWidth
              required
              label="Project Name"
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }));
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              error={!!errors.name}
              helperText={errors.name || ""}
            />

            {/* Project Type */}
            <TextField
              fullWidth
              label="Project Type"
              value={form.project_type}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  project_type: e.target.value,
                }))
              }
            />

            {/* Status */}
            <TextField
              select
              fullWidth
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
            >
              {PROJECT_STATUS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            {/* Target Devices */}
            <TextField
              fullWidth
              label="Target Devices"
              type="number"
              value={form.target_devices}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  target_devices: e.target.value,
                }))
              }
            />

            {/* Commissioned Devices */}
            <TextField
              fullWidth
              label="Commissioned Devices"
              type="number"
              value={form.commissioned_devices}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  commissioned_devices: e.target.value,
                }))
              }
            />

            {/* Dates full width */}
            <Box sx={{ gridColumn: { xs: "span 1", sm: "span 1" } }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={form.start_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </Box>

            <Box>
              <TextField
                fullWidth
                type="date"
                label="Target End Date"
                InputLabelProps={{ shrink: true }}
                value={form.target_end_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    target_end_date: e.target.value,
                  }))
                }
              />
            </Box>

            {/* Full width fields */}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                fullWidth
                label="Project Manager"
                value={form.project_manager}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    project_manager: e.target.value,
                  }))
                }
              />
            </Box>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Box>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2.5 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={() => setOpen(false)} fullWidth={fullScreenDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={Saving}
            fullWidth={fullScreenDialog}
            sx={{
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              position: "relative",
              minWidth: 140,
            }}
          >
            {Saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : editingId ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* delete model */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            width: { xs: "calc(100% - 32px)", sm: "100%" },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: "#DC2626" }}>
          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{SelctedProject?.name}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2.5 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            fullWidth={fullScreenDialog}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            fullWidth={fullScreenDialog}
            sx={{ position: "relative", minWidth: 120 }}
          >
            {deleting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
